import './browser-prelude'
import { ensureAmplitudeShim } from './browser-amplitude-shim'
import { Buffer } from 'buffer'
import { ensureBrowserAuthBootstrap, ensureBrowserAuthHelpers } from './browser-auth'
import { refreshBrowserBillingState } from './browser-billing'
import { ensureInitialBrowserStoreSnapshot } from './browser-initial-store'
import { buildBrowserBroadcastShim, buildIpcShim } from './browser-ipc-shim'
import { detectPlatform, getWindowType } from './browser-utils'
import { AppWindowType } from '@shared/types/WindowTypes'

const buildBrowserProcess = (): Window['process'] => {
  const isDevelopment = import.meta.env.DEV
  const nodeEnv: 'development' | 'production' = isDevelopment ? 'development' : 'production'

  return {
    contextId: 'browser',
    pid: 1,
    platform: detectPlatform(),
    type: 'renderer',
    env: {
      NODE_ENV: nodeEnv,
    },
  }
}

const isElectronRendererRuntime = (): boolean => {
  return (
    typeof window.process !== 'undefined' &&
    window.process?.type === 'renderer' &&
    window.process.contextId !== 'browser' &&
    typeof window.ipc?.channels !== 'undefined'
  )
}

const ensureProcessShims = (): void => {
  if (!window.process || window.process.type !== 'renderer') {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window as any).process = buildBrowserProcess()
    } catch (error) {
      console.warn('[browser-runtime] Failed to apply process shim', error)
    }
  } else if (!window.process.env) {
    try {
      window.process.env = buildBrowserProcess().env
    } catch (error) {
      console.warn('[browser-runtime] Failed to apply process.env shim', error)
    }
  }

  if (window.process?.env) {
    try {
      window.process.env.NODE_ENV = import.meta.env.DEV ? 'development' : 'production'
    } catch (error) {
      console.warn('[browser-runtime] Failed to update NODE_ENV', error)
    }
  }
}

const ensureIpcShim = (): void => {
  if (window.ipc?.channels) {
    window.ipc.broadcast ??= buildBrowserBroadcastShim()
    return
  }
  window.ipc = buildIpcShim()
}

const ensureWebFrameShim = (): void => {
  if (window.webFrame?.frameToken) {
    return
  }
  window.webFrame = {
    frameToken: 'browser-frame-token',
  }
}

const WINDOW_TYPE_MAP: Record<string, AppWindowType> = {
  settings: AppWindowType.SETTINGS,
  overlay: AppWindowType.OVERLAY,
  onboarding: AppWindowType.ONBOARDING,
}

const ensureHighlightShim = (): void => {
  if (window.highlight?.version && window.highlight?.channel) {
    return
  }
  window.highlight = {
    version: import.meta.env.VITE_HIGHLIGHT_VERSION ?? 'browser-dev',
    channel: import.meta.env.VITE_HIGHLIGHT_CHANNEL ?? 'latest',
    installedChannel: import.meta.env.VITE_HIGHLIGHT_CHANNEL ?? 'latest',
    windowId: 'browser-window',
    windowType: WINDOW_TYPE_MAP[getWindowType()] ?? AppWindowType.OVERLAY,
  }
}

const ensureBufferGlobal = (): void => {
  if (typeof globalThis.Buffer === 'undefined') {
    globalThis.Buffer = Buffer
  }
}

export const bootstrapBrowserRuntime = (): void => {
  if (isElectronRendererRuntime()) {
    return
  }
  ensureBufferGlobal()
  ensureProcessShims()
  ensureIpcShim()
  ensureWebFrameShim()
  ensureHighlightShim()
  ensureAmplitudeShim()
  ensureBrowserAuthHelpers()
  void ensureBrowserAuthBootstrap()
  ensureInitialBrowserStoreSnapshot()
  void refreshBrowserBillingState()
}

bootstrapBrowserRuntime()

if (import.meta.hot) {
  import.meta.hot.accept(() => {
    window.location.reload()
  })
}
